import { useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  USER_APPROVE_STATUS,
  USER_QUALIFY_STATUS,
  USER_ROLE,
} from '@/config/constants';
import {
  useGetCurrentUserQuery,
  useUpdateUserLoanInfoMutation,
  useUpdateUserMutation,
} from '@/apis/extended/userApi';
import {
  usePurchasingPowerBreakdown,
  usePurchasingPowerEstimation,
} from '@/hooks/usePurchasingPowerEstimation';
import { useStartLoanApplication } from '@/hooks/useStartLoanApplication';
import { formatPrice } from '@/utils/helper';
import { CommonFormControl } from '../Common/CommonFormControl';
import { CommonNumberInput } from '../Common/CommonNumberInput';
import { UserApproveStatusBadge } from './components/UserApproveStatusBadge';

export interface ProfileAdvancedSettingsProps {
  /** `embedded` suppresses the outer page heading & extra spacing so the
   *  component renders cleanly inside another panel. Defaults to `page`. */
  variant?: 'page' | 'embedded';
}

export const ProfileAdvancedSettings = ({
  variant: _variant = 'page',
}: ProfileAdvancedSettingsProps = {}) => {
  const { data, isFetching } = useGetCurrentUserQuery();
  const [updateUser] = useUpdateUserMutation();
  const [updateUserLoanInfo] = useUpdateUserLoanInfoMutation();

  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Only same-app paths, so a crafted link can't bounce anyone off-site.
  const returnTo = (() => {
    const raw = searchParams.get('returnTo');
    return raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : null;
  })();
  const { start: startApplication, isStarting } = useStartLoanApplication();
  const isQualified = data?.qualifyStatus === USER_QUALIFY_STATUS.QUALIFY;
  const [form, setForm] = useState({
    monthlyIncome: '',
    creditScore: '',
    downPayment: '',
    monthlyDebt: '',
    loanTerm: '',
  });

  const [lenderDefaultsForm, setLenderDefaultsForm] = useState({
    lenderName: '',
    lenderAddress: '',
    lenderNMLS: '',
    loanOfficerName: '',
    loanOfficerPhone: '',
    loanOfficerEmail: '',
    loanOfficerNMLS: '',
  });

  const purchasingPowerInputs = {
    monthlyIncome: Number(form.monthlyIncome),
    downPayment: Number(form.downPayment),
    creditScore: Number(form.creditScore),
    monthlyDebt: Number(form.monthlyDebt),
    loanTerm: Number(form.loanTerm),
  };
  const purchasingPower = usePurchasingPowerEstimation(purchasingPowerInputs);
  const purchasingPowerBreakdown = usePurchasingPowerBreakdown(
    purchasingPowerInputs
  );

  useEffect(() => {
    setForm({
      monthlyIncome: `${data?.loanInfo?.monthlyIncome || '0'}`,
      creditScore: `${data?.loanInfo?.creditScore || '0'}`,
      downPayment: `${data?.loanInfo?.downPayment || '0'}`,
      monthlyDebt: `${data?.loanInfo?.monthlyDebt || '0'}`,
      loanTerm: `${data?.loanInfo?.loanTerm || ''}`,
    });

    setLenderDefaultsForm({
      lenderName: data?.lenderDefaults?.lenderName || '',
      lenderAddress: data?.lenderDefaults?.lenderAddress || '',
      lenderNMLS: data?.lenderDefaults?.lenderNMLS || '',
      loanOfficerName: data?.lenderDefaults?.loanOfficerName || '',
      loanOfficerPhone: data?.lenderDefaults?.loanOfficerPhone || '',
      loanOfficerEmail: data?.lenderDefaults?.loanOfficerEmail || '',
      loanOfficerNMLS: data?.lenderDefaults?.loanOfficerNMLS || '',
    });
  }, [data]);

  const isUpdated = useMemo(() => {
    return (
      form.monthlyIncome !== data?.loanInfo?.monthlyIncome?.toString() ||
      form.creditScore !== data?.loanInfo?.creditScore?.toString() ||
      form.downPayment !== data?.loanInfo?.downPayment?.toString() ||
      form.monthlyDebt !== data?.loanInfo?.monthlyDebt?.toString() ||
      form.loanTerm !== data?.loanInfo?.loanTerm?.toString() ||
      lenderDefaultsForm.lenderName !==
        (data?.lenderDefaults?.lenderName || '') ||
      lenderDefaultsForm.lenderAddress !==
        (data?.lenderDefaults?.lenderAddress || '') ||
      lenderDefaultsForm.lenderNMLS !==
        (data?.lenderDefaults?.lenderNMLS || '') ||
      lenderDefaultsForm.loanOfficerName !==
        (data?.lenderDefaults?.loanOfficerName || '') ||
      lenderDefaultsForm.loanOfficerPhone !==
        (data?.lenderDefaults?.loanOfficerPhone || '') ||
      lenderDefaultsForm.loanOfficerEmail !==
        (data?.lenderDefaults?.loanOfficerEmail || '') ||
      lenderDefaultsForm.loanOfficerNMLS !==
        (data?.lenderDefaults?.loanOfficerNMLS || '')
    );
  }, [form, lenderDefaultsForm, data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setIsUpdating(true);

    updateUser({
      occupancy: data?.occupancy || undefined,
      loanProgram: data?.loanProgram || undefined,
      loanEstimationType: data?.loanEstimationType || undefined,
      ...(data?.role === USER_ROLE.ADMIN && {
        lenderDefaults: {
          lenderName: lenderDefaultsForm.lenderName,
          lenderAddress: lenderDefaultsForm.lenderAddress,
          lenderNMLS: lenderDefaultsForm.lenderNMLS,
          loanOfficerName: lenderDefaultsForm.loanOfficerName,
          loanOfficerPhone: lenderDefaultsForm.loanOfficerPhone,
          loanOfficerEmail: lenderDefaultsForm.loanOfficerEmail,
          loanOfficerNMLS: lenderDefaultsForm.loanOfficerNMLS,
        },
      }),
    });

    await updateUserLoanInfo({
      monthlyIncome: form.monthlyIncome
        ? parseFloat(form.monthlyIncome)
        : undefined,
      creditScore: form.creditScore ? parseInt(form.creditScore) : undefined,
      downPayment: form.downPayment ? parseFloat(form.downPayment) : undefined,
      monthlyDebt: form.monthlyDebt ? parseFloat(form.monthlyDebt) : undefined,
      loanTerm: parseInt(form.loanTerm),
    }).unwrap();

    setIsUpdating(false);
    // Came here from a property or application: go straight back instead of
    // leaving the borrower to find their way through Loan type → My
    // applications.
    if (returnTo) {
      toast.success('Saved — picking up where you left off');
      navigate(returnTo);
    }
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title={
          <>
            Income and Loan Settings{' '}
            <UserApproveStatusBadge
              sx={{ ml: 2 }}
              status={data?.approveStatus}
              size="small"
            />
          </>
        }
        avatar={<i className="fa-light fa-screwdriver-wrench" />}
      />
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Set your income and loan details. Transaction mode, loan program, and
          occupancy are set in <strong>Loan type</strong>.
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <CommonFormControl label="Monthly Income:" labelWidth={170}>
              <CommonNumberInput
                placeholder="Monthly Income"
                name="monthlyIncome"
                aria-label="Monthly income"
                required
                value={form.monthlyIncome}
                onChange={handleInputChange}
              />
            </CommonFormControl>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', pl: '178px', mt: -0.5 }}
            >
              Gross income before taxes, for all borrowers on the loan.
            </Typography>
            <CommonFormControl
              label="Credit Score:"
              sx={{ mt: 1 }}
              labelWidth={170}
            >
              <CommonNumberInput
                placeholder="Credit Score"
                name="creditScore"
                aria-label="Credit score"
                required
                value={form.creditScore}
                onChange={handleInputChange}
              />
            </CommonFormControl>
            <CommonFormControl
              label="Down Payment:"
              sx={{ mt: 1 }}
              labelWidth={170}
            >
              <CommonNumberInput
                placeholder="Down Payment"
                name="downPayment"
                aria-label="Down payment"
                required
                value={form.downPayment}
                onChange={handleInputChange}
              />
            </CommonFormControl>
            <CommonFormControl
              label="Monthly Debt:"
              sx={{ mt: 1 }}
              labelWidth={170}
            >
              <CommonNumberInput
                placeholder="Monthly Debt"
                name="monthlyDebt"
                aria-label="Monthly debt"
                required
                value={form.monthlyDebt}
                onChange={handleInputChange}
              />
            </CommonFormControl>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', pl: '178px', mt: -0.5 }}
            >
              Minimum required payments only: credit cards, auto loans, student
              loans, other mortgages. Not rent, utilities, or subscriptions.
            </Typography>
            <FormControlLabel
              sx={{ mt: 1, display: 'flex' }}
              control={
                <Checkbox
                  checked={Boolean(data?.isVeteran)}
                  onChange={(e) =>
                    void updateUser({ isVeteran: e.target.checked }).unwrap()
                  }
                />
              }
              label="I am a veteran or active-duty service member (VA loan eligibility)"
            />
            <CommonFormControl
              label="Loan Term:"
              sx={{ mt: 1 }}
              labelWidth={170}
            >
              <Select
                placeholder="Loan Term"
                name="loanTerm"
                value={form.loanTerm}
                onChange={handleSelectChange}
              >
                <MenuItem value="15">15 Years</MenuItem>
                <MenuItem value="30">30 Years</MenuItem>
              </Select>
            </CommonFormControl>
            <CommonFormControl
              label="Purchasing Power:"
              labelWidth={170}
              secondLabel={
                purchasingPower ? formatPrice(purchasingPower, 0) : '-'
              }
              sx={{ mt: 1 }}
            />
            {purchasingPowerBreakdown && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Assumes {purchasingPowerBreakdown.dtiCapPercent}% max
                debt-to-income at{' '}
                {purchasingPowerBreakdown.interestRate.toFixed(3)}
                %, including estimated principal & interest (
                {formatPrice(
                  purchasingPowerBreakdown.monthlyPrincipalAndInterest,
                  0
                )}
                /mo), property tax (
                {formatPrice(purchasingPowerBreakdown.monthlyPropertyTax, 0)}
                /mo), homeowners insurance (
                {formatPrice(purchasingPowerBreakdown.monthlyInsurance, 0)}
                /mo)
                {purchasingPowerBreakdown.monthlyPmi > 0 &&
                  `, and PMI (${formatPrice(
                    purchasingPowerBreakdown.monthlyPmi,
                    0
                  )}/mo, required above 80% loan-to-value)`}
                .
              </Typography>
            )}
            {data?.approveStatus === USER_APPROVE_STATUS.APPROVED &&
              data?.approvalAmount && (
                <CommonFormControl
                  label="Approval Amount:"
                  labelWidth={170}
                  secondLabel={formatPrice(data.approvalAmount)}
                  sx={{ mt: 1 }}
                />
              )}
          </Grid>
        </Grid>

        {data?.role === USER_ROLE.ADMIN && (
          <Box sx={{ mt: 4 }}>
            <Card variant="outlined">
              <CardHeader
                title="Lender Defaults (Auto-fill Buyer Offers)"
                subheader="These values are used to prefill the buyer's Offer Specifics lender/loan officer section."
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Lender
                    </Typography>
                    <TextField
                      fullWidth
                      label="Lender Name"
                      value={lenderDefaultsForm.lenderName}
                      onChange={(e) =>
                        setLenderDefaultsForm((p) => ({
                          ...p,
                          lenderName: e.target.value,
                        }))
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Lender Address"
                      value={lenderDefaultsForm.lenderAddress}
                      onChange={(e) =>
                        setLenderDefaultsForm((p) => ({
                          ...p,
                          lenderAddress: e.target.value,
                        }))
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Lender NMLS ID"
                      value={lenderDefaultsForm.lenderNMLS}
                      onChange={(e) =>
                        setLenderDefaultsForm((p) => ({
                          ...p,
                          lenderNMLS: e.target.value,
                        }))
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Loan Officer
                    </Typography>
                    <TextField
                      fullWidth
                      label="Loan Officer Name"
                      value={lenderDefaultsForm.loanOfficerName}
                      onChange={(e) =>
                        setLenderDefaultsForm((p) => ({
                          ...p,
                          loanOfficerName: e.target.value,
                        }))
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Loan Officer Phone"
                      value={lenderDefaultsForm.loanOfficerPhone}
                      onChange={(e) =>
                        setLenderDefaultsForm((p) => ({
                          ...p,
                          loanOfficerPhone: e.target.value,
                        }))
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Loan Officer Email"
                      value={lenderDefaultsForm.loanOfficerEmail}
                      onChange={(e) =>
                        setLenderDefaultsForm((p) => ({
                          ...p,
                          loanOfficerEmail: e.target.value,
                        }))
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Loan Officer NMLS ID"
                      value={lenderDefaultsForm.loanOfficerNMLS}
                      onChange={(e) =>
                        setLenderDefaultsForm((p) => ({
                          ...p,
                          loanOfficerNMLS: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}

        <CommonFormControl sx={{ mt: 1 }} labelWidth={170}>
          <LoadingButton
            loading={isFetching || isUpdating}
            disabled={!isUpdated}
            variant="contained"
            startIcon={<i className="fa-light fa-pencil" />}
            onClick={handleSave}
          >
            {returnTo ? 'Save and continue' : 'Save'}
          </LoadingButton>
        </CommonFormControl>
        {isQualified && !returnTo && !isUpdated && _variant === 'page' && (
          <Alert
            severity="success"
            sx={{ mt: 2 }}
            action={
              <LoadingButton
                color="inherit"
                size="small"
                loading={isStarting}
                onClick={() => void startApplication()}
              >
                Start my application
              </LoadingButton>
            }
          >
            You're pre-qualified
            {purchasingPower > 0
              ? ` for up to ${formatPrice(purchasingPower, 0)}`
              : ''}
            . Your application starts with these numbers filled in.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
